import { SortableContext } from "@dnd-kit/sortable";
import { Component } from "react";
import Select from "react-select";
import { Callout, CalloutIntent, LoadingSpinner, TriggerButton } from "widgets";
import { moveItemsInArray } from "widgets/utils";

import { type ISelectOption } from "@/app-utils/form.ts";
import { type IConfiguredAttribute } from "@/app-utils/playbook.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import { type Attribute, type RawAttributeDefault } from "@/graphql.ts";
import { COLUMN_SET, REPRESENTER_COLUMN, WORKFLOW_FIELD_TYPE_OPTIONS } from "@/settings/settings.ts";

import { SortableItem, SortableList } from "../../core/forms/Sortable.js";
import style from "../../core/forms/styles/Sortable.module.scss";
import AttributeInputConfigurationDialog from "./AttributeInputConfigurationDialog.js";

export interface IAttributesSelectorProps {
  selectedAttributes: IConfiguredAttribute[];
  availableAttributes: Attribute[];
  attributeDefaults?: Partial<RawAttributeDefault>[];
  attributesUpdated: (selectedParentAttributes: IConfiguredAttribute[]) => void;
  isLoadingAttributes: boolean;
  dropdownLabel?: string;
  disableReordering?: boolean;
  disableCustomization?: boolean;
}

interface IAttributesSelectorState {
  hint: string;
  attributeToConfigure: IConfiguredAttribute | null;
}

class AttributesSelector extends Component<IAttributesSelectorProps, IAttributesSelectorState> {
  constructor(props: IAttributesSelectorProps) {
    super(props);

    this.state = {
      hint: null,
      attributeToConfigure: null,
    };
  }

  checkList = (): string => {
    const suspiciouslyUnselectedAttributes = this.props.availableAttributes.filter(
      (attribute: IConfiguredAttribute) =>
        !this.props.selectedAttributes.find((s) => s.id === attribute.id) && // not selected
        !attribute.automatic && // not automatic
        this.props.attributeDefaults?.[attribute.id], // has default value from sample type configuration
    );
    if (suspiciouslyUnselectedAttributes.length) {
      return `Warning: ${pluralizeWord(
        "Attribute",
        suspiciouslyUnselectedAttributes.length,
      )} ${suspiciouslyUnselectedAttributes.map((a) => `"${a.attributeName}"`).join(", ")}
      ${suspiciouslyUnselectedAttributes.length === 1 ? " has a default value" : " have default values"}. A default will not be assigned to the attribute if it is not selected.
      Set an attribute to 'Automatic' if you want the default value to be assigned with no user interaction.`;
    }
  };

  componentDidMount() {
    this.setState({ hint: this.checkList() });
  }

  componentDidUpdate(prevProps: IAttributesSelectorProps) {
    if (prevProps.selectedAttributes !== this.props.selectedAttributes) {
      this.setState({ hint: this.checkList() });
    }
  }

  removeOption = (attribute: IConfiguredAttribute) => {
    const selectedParentAttributes: IConfiguredAttribute[] = this.props.selectedAttributes.filter(
      (selectedAttribute: IConfiguredAttribute) => selectedAttribute.id !== attribute.id,
    );
    this.props.attributesUpdated(selectedParentAttributes);
  };

  addOption = (attribute: IConfiguredAttribute) => {
    const selectedParentAttributes: IConfiguredAttribute[] = this.props.selectedAttributes.slice(0);
    selectedParentAttributes.push(attribute);
    this.props.attributesUpdated(selectedParentAttributes);
  };

  updateOption = (configuredAttribute: IConfiguredAttribute) => {
    const selectedParentAttributes: IConfiguredAttribute[] = this.props.selectedAttributes.map((selectedAttribute) =>
      selectedAttribute.id === configuredAttribute.id ? configuredAttribute : selectedAttribute,
    );
    this.props.attributesUpdated(selectedParentAttributes);
  };

  createLabel(attribute) {
    return `${attribute.label || attribute.attributeName}${attribute.groupName ? ` (${attribute.groupName})` : ""}`;
  }

  render() {
    return (
      <div className={"attributes-selector"}>
        {this.state.hint && <Callout intent={CalloutIntent.Warning}>{this.state.hint} </Callout>}

        {this.props.isLoadingAttributes && <LoadingSpinner />}
        <SortableList
          id="dataseries-list"
          onDragEnd={({ active, over }) =>
            this.props.attributesUpdated(moveItemsInArray(this.props.selectedAttributes, active, over))
          }
        >
          <SortableContext items={this.props.selectedAttributes}>
            {this.props.selectedAttributes.map((attribute: IConfiguredAttribute, index: number) => {
              const label: string = this.createLabel(attribute);
              const attributeType: ISelectOption = WORKFLOW_FIELD_TYPE_OPTIONS.find(
                (option: ISelectOption) => (attribute.correctedTypeId || attribute.attributeType.id) === option.value,
              );
              const isSampleNameAttribute: boolean =
                attribute.representedColumn === REPRESENTER_COLUMN.NAME &&
                attribute.columnSet.id === COLUMN_SET.COS_ANNOTATION_ID.id;
              // Sample name representer attribute is never removable.
              // Other attributes are removable if they are optional or have a default (including the parent name attribute).
              const attributeIsRemovable: boolean =
                !isSampleNameAttribute &&
                (!(attribute.mandatory || attribute.correctedMandatory) ||
                  !!this.props.attributeDefaults?.[attribute.id]);
              const removeTooltip: string | undefined = attributeIsRemovable
                ? undefined
                : `You can't remove this attribute because it is ${
                    isSampleNameAttribute ? 'the "name" attribute' : "mandatory and does not have a default value"
                  }.`;

              return (
                <SortableItem
                  key={`attrib-${attribute.id}`}
                  id={attribute.id}
                  index={index}
                  label={label}
                  onRemove={attributeIsRemovable ? () => this.removeOption(attribute) : null}
                  removeTooltip={removeTooltip}
                  disabled={this.props.disableReordering}
                >
                  <>
                    {!this.props.disableCustomization && (
                      <TriggerButton
                        onPress={() => this.setState({ attributeToConfigure: attribute })}
                        label={`Configure ${label}`}
                        tooltip={"Configure some properties of this input field as shown during Playbook execution"}
                        icon={"edit"}
                      />
                    )}
                    <span className={style.label}>
                      {label}
                      <div className={style.sub_label}>{`${
                        attribute.isWorkflowParam ? "Analysis Parameter, " : ""
                      } ${attributeType?.label}, ${
                        attribute.mandatory || attribute.correctedMandatory ? "mandatory" : "optional"
                      }`}</div>
                    </span>
                  </>
                </SortableItem>
              );
            })}
          </SortableContext>
        </SortableList>

        <div style={{ marginTop: "10px" }}>
          <Select
            isClearable={false}
            isSearchable={true}
            getOptionValue={(option: IConfiguredAttribute) => option.id}
            getOptionLabel={(option: IConfiguredAttribute) => option.attributeName}
            isDisabled={false}
            isLoading={this.props.isLoadingAttributes}
            options={this.props.availableAttributes.filter(
              (attribute: IConfiguredAttribute) =>
                !this.props.selectedAttributes.find(
                  (selectedAttribute: IConfiguredAttribute) => selectedAttribute.id === attribute.id,
                ),
            )}
            //hideSelectedOptions={true}
            classNamePrefix={"react-select"}
            placeholder={this.props.dropdownLabel}
            onChange={this.addOption}
            noOptionsMessage={() => "No available attribute..."}
            controlShouldRenderValue={false}
            onBlurResetsInput={true}
            menuPosition={"absolute"}
            menuPortalTarget={document.body}
            menuShouldScrollIntoView={false}
            styles={{ menuPortal: (provided) => ({ ...provided, zIndex: 9999 }) }}
          />
        </div>

        {this.state.attributeToConfigure && (
          <AttributeInputConfigurationDialog
            onApply={(attribute: IConfiguredAttribute) => {
              this.setState({ attributeToConfigure: null });
              this.updateOption(attribute);
            }}
            onCancel={() => this.setState({ attributeToConfigure: null })}
            attribute={this.state.attributeToConfigure}
            allAttributes={this.props.selectedAttributes}
          />
        )}
      </div>
    );
  }
}

export default AttributesSelector;
